/* Zwei Wege Section – Sacred Modern Design
 * Light section showing two paths: AYU Standard vs. AYU + DNA
 * Side-by-side comparison cards — keine Preise, CTA → Beratungsgespräch
 */
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BookOpen, Dna, Check, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const paths = [
  {
    icon: BookOpen,
    titleKey: "landing.zweiWege.path1Title",
    subtitleKey: "landing.zweiWege.path1Subtitle",
    descriptionKey: "landing.zweiWege.path1Desc",
    featureKeys: [
      "landing.zweiWege.path1F1",
      "landing.zweiWege.path1F2",
      "landing.zweiWege.path1F3",
      "landing.zweiWege.path1F4",
      "landing.zweiWege.path1F5",
      "landing.zweiWege.path1F6",
      "landing.zweiWege.path1F7",
      "landing.zweiWege.path1F8",
    ],
    highlight: false,
  },
  {
    icon: Dna,
    titleKey: "landing.zweiWege.path2Title",
    subtitleKey: "landing.zweiWege.path2Subtitle",
    descriptionKey: "landing.zweiWege.path2Desc",
    featureKeys: [
      "landing.zweiWege.path2F1",
      "landing.zweiWege.path2F2",
      "landing.zweiWege.path2F3",
      "landing.zweiWege.path2F4",
      "landing.zweiWege.path2F5",
    ],
    highlight: true,
  },
];

export default function ZweiWegeSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="programme" className="section-light py-24 lg:py-32 relative" ref={ref}>
      <div className="gold-line w-full absolute top-0" />

      <div className="container">
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("landing.zweiWege.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.25_0.04_50)] mt-4 mb-6">
            {t("landing.zweiWege.heading1")} <span className="text-[oklch(0.52_0.09_128)]">{t("landing.zweiWege.heading2")}</span>
          </h2>
          <p className="text-lg text-[oklch(0.45_0.04_50)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("landing.zweiWege.description")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {paths.map((path, i) => (
            <div
              key={i}
              className={`relative rounded-2xl p-8 transition-all duration-700 ${
                path.highlight
                  ? "bg-[oklch(0.18_0.02_280)] text-[oklch(0.93_0.015_80)] border-2 border-[oklch(0.52_0.09_128/0.4)] shadow-xl"
                  : "bg-white border border-[oklch(0.88_0.02_80)] shadow-sm"
              } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 200 + 200}ms` }}
            >
              {path.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[oklch(0.52_0.09_128)] rounded-full text-xs font-semibold text-white tracking-wide">
                  {t("landing.zweiWege.recommended")}
                </div>
              )}

              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                path.highlight ? "bg-[oklch(0.52_0.09_128/0.2)]" : "bg-[oklch(0.52_0.09_128/0.1)]"
              }`}>
                <path.icon className="text-[oklch(0.52_0.09_128)]" size={26} />
              </div>

              <h3 className="text-2xl font-bold mb-1">{t(path.titleKey as any)}</h3>
              <p className="text-sm mb-4 text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
                {t(path.subtitleKey as any)}
              </p>

              <p className={`mb-6 ${path.highlight ? "text-[oklch(0.7_0.02_80)]" : "text-[oklch(0.5_0.03_50)]"}`} style={{ fontFamily: "var(--font-body)" }}>
                {t(path.descriptionKey as any)}
              </p>

              <ul className="space-y-3">
                {path.featureKeys.map((featureKey, j) => (
                  <li key={j} className="flex items-center gap-3">
                    <Check className="text-[oklch(0.52_0.09_128)] shrink-0" size={16} />
                    <span className={`text-sm ${path.highlight ? "text-[oklch(0.8_0.01_80)]" : "text-[oklch(0.45_0.04_50)]"}`} style={{ fontFamily: "var(--font-body)" }}>
                      {t(featureKey as any)}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="/termin"
                className={`flex items-center justify-center gap-2 w-full mt-8 py-3 rounded-full text-center font-semibold transition-all duration-300 ${
                  path.highlight
                    ? "btn-olive"
                    : "border border-[oklch(0.52_0.09_128)] text-[oklch(0.52_0.09_128)] hover:bg-[oklch(0.52_0.09_128/0.05)]"
                }`}
              >
                {t("landing.zweiWege.ctaButton")}
                <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-[oklch(0.55_0.03_50)] mt-8" style={{ fontFamily: "var(--font-body)" }}>
          {t("landing.zweiWege.footnote")}
        </p>
      </div>
    </section>
  );
}
